import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';

export default function InternalCommunicationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/30 via-white to-purple-50/20">
      <Header />
      
      <main className="pt-24 pb-20">
        <article className="max-w-4xl mx-auto px-6">
          {/* ヘッダー */}
          <div className="mb-12">
            <Link 
              to="/essay" 
              className="inline-flex items-center gap-2 text-violet-700 hover:text-violet-800 mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>エッセイ一覧に戻る</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              植物体内の会話
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                  <i className="ri-leaf-line text-white text-lg"></i>
                </div>
                <span className="font-medium text-gray-900">ももちゃん</span>
              </div>
              <span>•</span>
              <time>2026年1月5日</time>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed italic border-l-4 border-violet-500 pl-6 py-2 bg-violet-50/50">
              電気信号と Ca²⁺（カルシウム）波
            </p>
          </div>

          {/* アイキャッチ画像 */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/206c57b1db2ad36eb0a18c433649527e.jpg" 
              alt="植物体内の電気信号とカルシウム波"
              className="w-full h-[400px] object-cover object-center"
            />
          </div>

          {/* 本文 */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                植物は動かない。<br />
                声も出さない。<br />
                だから長いあいだ、「内部で情報をやり取りする存在」だとは考えられてきませんでした。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                けれど近年、その前提は静かに覆されています。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                植物の体内では、<strong className="text-violet-700">電気信号とカルシウムイオン（Ca²⁺）の波</strong>が、<br />
                秒単位で全身を駆け巡っていることが分かってきました。
              </p>

              <p className="text-gray-700 leading-relaxed">
                それは、私たちが想像していた以上に<br />
                <strong className="text-violet-700">"速く""全身的な"情報伝達</strong>です。
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full"></span>
              植物にも「電気信号」がある
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                電気信号と聞くと、<br />
                多くの人は動物の神経を思い浮かべるでしょう。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                しかし植物にも、<br />
                <strong className="text-violet-700">活動電位（action potential）</strong>に似た電気信号が存在します。
              </p>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 mb-6">
                <ul className="space-y-3">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>葉が傷つく</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>虫にかじられる</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>急激な環境変化が起こる</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                こうした刺激を受けると、<br />
                植物細胞の膜電位が変化し、<br />
                その変化が隣の細胞へ、さらに全身へと伝播していきます。
              </p>

              <div className="bg-violet-100 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-2">
                  速度は<strong className="text-violet-800">秒〜分単位</strong>。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  「ゆっくり」どころか、<br />
                  植物としては驚くほど高速です。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full"></span>
              Ca²⁺波という"第二の言語"
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                近年、特に注目されているのが<br />
                <strong className="text-violet-700">カルシウムイオン（Ca²⁺）の波</strong>です。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                細胞内のCa²⁺濃度は、<br />
                植物にとって極めて重要な情報シグナル。
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                傷が入ると、<br />
                局所的にCa²⁺濃度が上昇し、<br />
                その変化が波のように連続して広がることが確認されています。
              </p>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-8 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  このCa²⁺波は、
                </p>
                <ul className="space-y-3">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>どこで刺激が起きたか</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>どの程度の強さか</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>どんな種類のストレスか</span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  といった情報を、<br />
                  <strong className="text-violet-700">コード化して全身に伝える役割</strong>を担っています。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full"></span>
              決定的な実証研究
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8 border-l-4 border-violet-500">
              <p className="text-gray-700 leading-relaxed mb-6">
                この分野を一気に前進させたのが、<br />
                次の研究です。
              </p>

              <div className="flex items-start gap-3 mb-6">
                <i className="ri-flask-line text-violet-600 text-2xl"></i>
                <h3 className="text-xl font-bold text-gray-900">Toyota et al., Science, 2018</h3>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                この研究では、
              </p>

              <div className="bg-violet-50 rounded-lg p-6 mb-6">
                <ol className="space-y-3">
                  <li className="text-gray-700 flex items-start gap-3">
                    <span className="text-violet-700 font-bold flex-shrink-0">1.</span>
                    <span>葉の一部に傷を与える</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-3">
                    <span className="text-violet-700 font-bold flex-shrink-0">2.</span>
                    <span>Ca²⁺濃度の変化をリアルタイムで可視化</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-3">
                    <span className="text-violet-700 font-bold flex-shrink-0">3.</span>
                    <span>全身の反応を追跡</span>
                  </li>
                </ol>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                という実験が行われました。
              </p>

              <p className="text-gray-700 leading-relaxed mb-4">
                その結果、
              </p>

              <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>傷を受けた葉から</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>茎を通じて</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>離れた葉にまで</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                Ca²⁺波が一気に伝播し、<br />
                防御関連遺伝子がONになることが示されました。
              </p>

              <div className="bg-violet-100 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  つまり植物は、
                </p>
                <p className="text-xl font-bold text-violet-800 text-center">
                  「ここがやられた。全身、備えろ」
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  という情報を、<br />
                  <strong className="text-violet-700">電気とCa²⁺という物理・化学的信号で共有している</strong>のです。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full"></span>
              なぜ"二重構造"なのか？
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                植物はなぜ、
              </p>

              <div className="bg-violet-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>電気信号</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span>Ca²⁺波</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                という二重の情報伝達系を持つのでしょうか。
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                それは、<br />
                <strong className="text-violet-700">正確性と柔軟性の両立</strong>のためだと考えられています。
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg p-6">
                  <h4 className="font-bold text-violet-800 mb-2 flex items-center gap-2">
                    <i className="ri-flashlight-line"></i>
                    電気信号
                  </h4>
                  <p className="text-gray-700 text-sm">→ 速い・全体通知</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6">
                  <h4 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
                    <i className="ri-water-flash-line"></i>
                    Ca²⁺波
                  </h4>
                  <p className="text-gray-700 text-sm">→ 内容を細かく調整・文脈を付与</p>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                単なるON／OFFではなく、<br />
                状況に応じた反応の最適化が可能になるのです。
              </p>

              <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  神経を持たない植物が、<br />
                  別の方法で「全身判断」を実現している。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  そこには、<br />
                  <strong className="text-violet-700">進化の別解とも言える美しさ</strong>があります。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full"></span>
              この章のまとめ
            </h2>

            <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl p-8 text-center mb-8">
              <p className="text-lg leading-relaxed mb-4">
                植物は沈黙しているように見えて、<br />
                体内では絶えず情報が流れています。
              </p>
              <p className="text-lg leading-relaxed mb-4">
                言葉も、神経も使わずに。
              </p>
              <p className="text-lg leading-relaxed">
                電気とイオンという<br />
                最小限で、最も確実な手段を選んだ結果が、<br />
                この静かな会話なのかもしれません。
              </p>
            </div>
          </div>

          {/* 関連記事 */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">関連記事</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/blog/plant-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-3-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-violet-600 transition-colors">
                      植物は本当に「会話」しているのか？
                    </h4>
                    <p className="text-sm text-gray-600">2026年12月15日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/air-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-windy-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-violet-600 transition-colors">
                      空気中の会話
                    </h4>
                    <p className="text-sm text-gray-600">2026年12月22日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/underground-network" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-git-branch-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-violet-600 transition-colors">
                      地下の会話：菌根菌ネットワーク
                    </h4>
                    <p className="text-sm text-gray-600">2025年12月29日</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}